import {
  IonButton,
  IonButtons,
  IonIcon,
  IonRouterContext,
  IonToolbar,
  useIonActionSheet,
} from '@ionic/react';
import {
  arrowDownOutline,
  copyOutline,
  createOutline,
  removeCircleOutline,
  trash,
} from 'ionicons/icons';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useHasPermission } from '../../../api/authorization.provider';
import {
  PlanEntitlementKey,
  Scratchpad,
  ScratchpadPlaylistItem,
  ScratchpadPlaylistItemPartsFragment,
  useGetScratchpadQuery,
} from '../../../api/graphql/generated';
import { trackPlaylistToggledAutoplay } from '../../../config/analytics';
import i18n from '../../../i18n';
import {
  useIsMachineActive,
  useMachineContext,
} from '../../../pages/machine/machine-context-provider';
import { useBeaconRouter } from '../../../route/route.hooks';
import { PaywallLoadingButton } from '../../paywall/paywall-loading-button';
import { ScratchpadAttachmentActionButton } from '../scratchpad/scratchpad-attachment-action-button';
import { ScratchpadCardBase } from '../scratchpad/scratchpad-card-base';
import './playlist.css';
import {
  ScratchpadPlaylistStatus,
  useScratchpadPlaylistHelpers,
  useScratchpadPlaylistToggleAutoplay,
} from './scratchpad-playlist.hooks';

export interface ScratchpadPlaylistItemProps {
  index: number;
  item: ScratchpadPlaylistItemPartsFragment;
  onClick?: (scratchpad: Scratchpad) => void;
  status?: ScratchpadPlaylistStatus;
  teamId: string;
}

const ScratchpadPlaylistItemComponent = (
  props: ScratchpadPlaylistItemProps
): ReactElement => {
  const { index, item, teamId, status } = props;

  const { data } = useGetScratchpadQuery({
    variables: { input: { id: item.scratchpadId } },
    fetchPolicy: 'cache-first',
  });

  const [saving, setSaving] = useState<boolean>(false);

  const scratchpad = data?.getScratchpad;

  const canUpdateScratchpad = useHasPermission(
    'update',
    'scratchpad',
    scratchpad
  );

  const { hideAttachments } = useMachineContext();
  const isMachineActive = useIsMachineActive();

  const canUpdate = canUpdateScratchpad && isMachineActive;

  const { push } = useContext(IonRouterContext);
  const { builder } = useBeaconRouter();

  const [confirmActionSheet, _dismissConfirmActionSheet] = useIonActionSheet();

  const {
    updateActivatedPlaylistItem,
    removePlaylistItem,
    addScratchpadToActivatedPlaylist,
  } = useScratchpadPlaylistHelpers({
    machineId: scratchpad?.machineId,
  });

  let name = scratchpad?.name;
  if (item.quantity !== 1) {
    name += ` (${i18n.t('common:scratchpad_playlist_qty')} ${item.quantity})`;
  }

  const rightElement = useMemo(() => {
    if (status?.label) {
      return (
        <div className="playlist-item-subtitle-highlight">{status.label}</div>
      );
    }

    return null;
  }, [status]);

  const toggleAutoPlayToNext = useCallback(
    async (item: ScratchpadPlaylistItem) => {
      setSaving(true);
      const autoPlayToNext = !item.autoPlayToNext;
      await updateActivatedPlaylistItem({
        ...item,
        autoPlayToNext,
      });
      trackPlaylistToggledAutoplay({ autoPlayToNext });
      setSaving(false);
    },
    [updateActivatedPlaylistItem]
  );

  const canTogglePlaylistAutoplay = useScratchpadPlaylistToggleAutoplay();

  const footer = (
    <IonToolbar className="playlist-actions-container">
      <IonButtons slot="start">
        <IonButton
          fill="clear"
          size="large"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            confirmActionSheet([
              {
                text: `${
                  canUpdate ? i18n.t('common:edit') : i18n.t('common:view')
                } "${scratchpad?.name}"`,
                handler: () => {
                  if (scratchpad?.id && scratchpad?.machineId) {
                    push(
                      builder.getMachineScratchpadRoute(
                        teamId,
                        scratchpad.machineId,
                        scratchpad.id
                      )
                    );
                  }
                },
              },
              { text: i18n.t('common:cancel'), role: 'cancel' },
            ]);
          }}
        >
          <IonIcon icon={createOutline} color="medium" />
        </IonButton>
        {!hideAttachments && (
          <ScratchpadAttachmentActionButton
            scratchpad={scratchpad}
            iconProps={{
              color: 'medium',
            }}
          />
        )}
        <IonButton
          fill="clear"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            addScratchpadToActivatedPlaylist(item.scratchpadId, {
              addAtIndex: index + 1,
            });
          }}
        >
          <IonIcon icon={copyOutline} color="medium" />
        </IonButton>
        <IonButton
          fill="clear"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            confirmActionSheet(
              [
                {
                  text: `${i18n.t(
                    'common:scratchpad_playlist_yes_remove'
                  )} "${scratchpad?.name}"`,
                  role: 'destructive',
                  handler: () => removePlaylistItem(item.id),
                },
                { text: i18n.t('common:cancel'), role: 'cancel' },
              ],
              i18n.t('common:scratchpad_playlist_confirm_removal')
            );
          }}
        >
          <IonIcon icon={trash} color="medium" />
        </IonButton>
      </IonButtons>
      <IonButtons className="ion-justify-content-center" slot="end">
        <PaywallLoadingButton
          planEntitlementKey={PlanEntitlementKey.ENHANCED_PLAYLIST}
          isFeatureAvailable={() => canTogglePlaylistAutoplay}
          loadingButtonProps={{
            loading: saving,
            setWidthOnInitializationOnly: true,
            spinnerProps: {
              name: 'circles',
            },
            buttonProps: {
              fill: 'clear',
              onClick: async (e) => {
                e.preventDefault();
                e.stopPropagation();
                toggleAutoPlayToNext(item);
              },
              disabled: status?.isLast,
            },
          }}
        >
          {getAutoplayIcon(item.autoPlayToNext, status?.isLast ?? false)}
        </PaywallLoadingButton>
      </IonButtons>
    </IonToolbar>
  );

  return (
    <ScratchpadCardBase
      className={
        status?.isLoaded ? 'playlist-item-border' : 'playlist-item-no-border'
      }
      scratchpad={scratchpad as Scratchpad}
      title={name}
      rightSubtitle={rightElement}
      footer={footer}
    />
  );
};

const getAutoplayIcon = (
  autoPlayToNext: boolean | null | undefined,
  isLast: boolean
): JSX.Element => {
  let icon: string | undefined;
  let color: string | undefined;
  let className: string | undefined;

  if (autoPlayToNext && !isLast) {
    icon = arrowDownOutline;
    color = 'success';
  } else {
    icon = removeCircleOutline;
    color = isLast ? 'medium' : 'danger';
    className = 'playlist-action-stop';
  }

  return (
    <IonIcon
      // add the class name as a key since we might be doing rotation on the icon
      // occasionally the incorrect icon has a rotation applied and it seems to be
      // related to the previous state
      key={className}
      className={className}
      icon={icon}
      color={color}
    />
  );
};

export { ScratchpadPlaylistItemComponent, getAutoplayIcon };
